import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { ThemeProvider, theme, Container, Text, Box, Stack, Button, Table, TableRow, TableCell, Tag, Popover, PopoverItem } from '@hub-la/design-system';
import AddIcon from '@mui/icons-material/Add';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CollaboratorsListEmptyState } from '../../components/collaborators-list/empty-state.js';
import { ConfirmDeleteModal } from '../../components/confirm-delete-modal.js';
import { RemainingPermissions } from '../../components/remaining-permissions.js';
import { useDeleteCollaborator } from '../../hooks/use-delete-collaborator.js';
import { useGetMyCollaborators } from '../../hooks/use-get-my-collaborators.js';

const TableCollaborators = ({
  rows,
  refetch
}) => {
  const history = useHistory();
  const {
    t
  } = useTranslation();
  const [deleteModal, setIsDeleteModal] = useState({
    isOpen: false,
    collaboratorId: '',
    collaboratorName: ''
  });
  const {
    mutateAsync: deleteCollaborator,
    isLoading
  } = useDeleteCollaborator({
    onSuccess: () => {
      setIsDeleteModal({
        isOpen: false,
        collaboratorId: '',
        collaboratorName: ''
      });
      refetch();
    }
  });
  const headCells = [{
    key: 'name',
    label: t('collaboratorsList.table.head.name'),
    sortable: false,
    width: 100
  }, {
    key: 'email',
    label: t('collaboratorsList.table.head.email'),
    sortable: false,
    width: 100
  }, {
    key: 'permissions',
    label: t('collaboratorsList.table.head.permissions'),
    sortable: false,
    width: 250
  }, {
    key: 'actions',
    label: t('collaboratorsList.table.head.actions'),
    sortable: false,
    width: 30
  }];
  return jsxs(Box, Object.assign({
    mt: 8
  }, {
    children: [jsx(Stack, Object.assign({
      alignItems: "end",
      mb: 4
    }, {
      children: jsx(Button, Object.assign({
        startIcon: jsx(AddIcon, {}),
        onClick: () => history.push('/collaborators/create')
      }, {
        children: t('collaboratorsList.table.button')
      }))
    })), jsx(ConfirmDeleteModal, {
      isOpen: deleteModal === null || deleteModal === void 0 ? void 0 : deleteModal.isOpen,
      onClose: () => setIsDeleteModal({
        isOpen: !deleteModal,
        collaboratorId: '',
        collaboratorName: ''
      }),
      onConfirm: () => {
        deleteCollaborator(deleteModal === null || deleteModal === void 0 ? void 0 : deleteModal.collaboratorId);
      },
      isDeleting: isLoading,
      collaboratorName: deleteModal === null || deleteModal === void 0 ? void 0 : deleteModal.collaboratorName
    }), jsx(Table, Object.assign({
      headCells: headCells,
      id: "collaborators",
      maxHeight: "100%",
      bgHeadCell: "#fff"
    }, {
      children: (rows !== null && rows !== void 0 ? rows : []).map(row => jsxs(TableRow, Object.assign({
        hover: true,
        role: "checkbox",
        "aria-disabled": "true"
      }, {
        children: [jsx(TableCell, Object.assign({
          padding: "none",
          scope: "row"
        }, {
          children: jsx(Text, {
            children: row === null || row === void 0 ? void 0 : row.name
          })
        })), jsx(TableCell, Object.assign({
          padding: "none",
          scope: "row"
        }, {
          children: jsx(Text, {
            children: row === null || row === void 0 ? void 0 : row.email
          })
        })), jsx(TableCell, Object.assign({
          padding: "none",
          scope: "row"
        }, {
          children: jsx(RemainingPermissions, Object.assign({
            data: row.permissions
          }, {
            children: row.permissions.slice(0, 4).map(permission => jsx(Tag, {
              label: t('permissions.' + permission),
              size: "small",
              variant: "neutral"
            }))
          }))
        })), jsx(TableCell, Object.assign({
          scope: "row",
          padding: "none",
          density: "compact"
        }, {
          children: jsx(Stack, Object.assign({
            flexDirection: "row"
          }, {
            children: jsx(Popover, Object.assign({
              items: ({
                close
              }) => jsxs(Stack, {
                children: [jsx(PopoverItem, Object.assign({
                  onClick: () => {
                    close();
                    history.push(`/collaborators/${row === null || row === void 0 ? void 0 : row.userId}`);
                  }
                }, {
                  children: jsx(Text, {
                    children: t('collaboratorsList.table.actions.edit')
                  })
                })), jsx(PopoverItem, Object.assign({
                  onClick: () => {
                    close();
                    setIsDeleteModal({
                      isOpen: !(deleteModal === null || deleteModal === void 0 ? void 0 : deleteModal.isOpen),
                      collaboratorId: row === null || row === void 0 ? void 0 : row.userId,
                      collaboratorName: row === null || row === void 0 ? void 0 : row.name
                    });
                  }
                }, {
                  children: jsx(Text, {
                    children: t('collaboratorsList.table.actions.delete')
                  })
                }))]
              })
            }, {
              children: jsx(Button, Object.assign({
                iconOnly: true,
                size: "small",
                variant: "outlined"
              }, {
                children: jsx(MoreHoriz, {})
              }))
            }))
          }))
        }))]
      }), row === null || row === void 0 ? void 0 : row.userId))
    }))]
  }));
};
const CollaboratorsPage = () => {
  var _a;
  const {
    t
  } = useTranslation();
  const {
    data,
    isLoading,
    isFetching,
    refetch
  } = useGetMyCollaborators();
  const isEmpty = ((_a = data !== null && data !== void 0 ? data : []) === null || _a === void 0 ? void 0 : _a.length) === 0;
  return jsx(ThemeProvider, Object.assign({
    theme: theme.light
  }, {
    children: jsxs(Container, {
      children: [jsx(Text, Object.assign({
        variant: "h3",
        mb: 3
      }, {
        children: t('collaboratorsList.title')
      })), jsx(Text, Object.assign({
        variant: "body2",
        mb: 4
      }, {
        children: t('collaboratorsList.description')
      })), isEmpty && jsx(CollaboratorsListEmptyState, {
        isLoading: isFetching || isLoading
      }, "collaborators-empty-state"), !isEmpty && jsx(TableCollaborators, {
        rows: data,
        refetch: refetch
      }, "table-collaborators")]
    })
  }));
};

export { CollaboratorsPage };
